import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";

import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Grid,
  MenuItem,
  Chip,
  Divider,
  Stack,
  InputAdornment,
  CircularProgress,
} from "@mui/material";

import {
  Edit,
  Delete,
  Download,
  Search,
  Close,
  Save,
  CalendarMonth,
  MenuBook,
  School,
  Assessment,
} from "@mui/icons-material";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import logo from "./savationChristLogo.png";

/*
|--------------------------------------------------------------------------
| THEME
|--------------------------------------------------------------------------
*/

const COLORS = {
  black: "#111111",
  darkBlack: "#181818",
  orange: "#F28C28",
  darkOrange: "#D96F0B",
  camel: "#C19A6B",
  lightCamel: "#F4E8D8",
  cream: "#FBF8F3",
  white: "#FFFFFF",
  gray: "#6B7280",
  lightGray: "#F5F5F5",
  border: "#E5E7EB",
  green: "#2E7D32",
  red: "#C62828",
};

/*
|--------------------------------------------------------------------------
| PDF STYLES
|--------------------------------------------------------------------------
*/

const pdfStyles = StyleSheet.create({
  page: {
    padding: 28,
    backgroundColor: "#FBF8F3",
    fontFamily: "Helvetica",
  },

  header: {
    backgroundColor: "#d6d5d4",
    padding: 18,
    borderRadius: 8,
    marginBottom: 18,
    flexDirection: "row",
    alignItems: "center",
  },

  logo: {
    width: 62,
    height: 62,
    marginRight: 15,
  },

  headerContent: {
    flex: 1,
  },

  schoolName: {
    color: "#000000",
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 5,
  },

  schoolInfo: {
    color: "#000000",
    fontSize: 8,
    marginBottom: 3,
  },

  title: {
    color: "#2f1801",
    fontSize: 15,
    fontWeight: "bold",
    marginTop: 7,
  },

  metadata: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 15,
  },

  metadataItem: {
    backgroundColor: "#F4E8D8",
    padding: 8,
    marginRight: 8,
    marginBottom: 8,
    borderRadius: 5,
  },

  metadataLabel: {
    fontSize: 7,
    color: "#6B7280",
    marginBottom: 2,
  },

  metadataValue: {
    fontSize: 9,
    color: "#111111",
    fontWeight: "bold",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ffae45",
    color: "#000000",
    paddingVertical: 8,
    paddingHorizontal: 5,
    fontSize: 7,
    fontWeight: "bold",
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    minHeight: 42,
  },

  tableRowAlt: {
    backgroundColor: "#FFFFFF",
  },

  cell: {
    padding: 6,
    fontSize: 7,
    color: "#222222",
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
  },

  topic: {
    width: "12%",
  },

  subject: {
    width: "10%",
  },

  class: {
    width: "9%",
  },

  period: {
    width: "9%",
  },

  week: {
    width: "6%",
  },

  objectives: {
    width: "17%",
  },

  content: {
    width: "17%",
  },

  method: {
    width: "10%",
  },

  assessment: {
    width: "10%",
  },

  footer: {
    position: "absolute",
    bottom: 18,
    left: 28,
    right: 28,
    borderTopWidth: 1,
    borderTopColor: "#C19A6B",
    paddingTop: 7,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 8,
    color: "#6B7280",
  },
});

/*
|--------------------------------------------------------------------------
| LESSON PLAN PDF
|--------------------------------------------------------------------------
*/

const LessonPlanPDF = ({
  lessonPlans,
  periodName = "All Periods",
}) => {
  const teachers = [
    ...new Set(
      lessonPlans
        .map((plan) => plan.teacher_name)
        .filter(Boolean)
    ),
  ];

  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={pdfStyles.page}
      >
        {/* HEADER */}
        <View style={pdfStyles.header}>
          <Image
            src={logo}
            style={pdfStyles.logo}
          />

          <View style={pdfStyles.headerContent}>
            <Text style={pdfStyles.schoolName}>
              Salvation In Christ School System
              
            </Text>

            <Text style={pdfStyles.schoolInfo}>
              Location: Zazay Community, Paynesville City, Monrovia
              
            </Text>

            <Text style={pdfStyles.schoolInfo}>
              Contact: +231-886909466 / +231-770123025
            </Text>

            <Text style={pdfStyles.schoolInfo}>
              Email: salvationinchristministries69@gmail.com
            </Text>

            <Text style={pdfStyles.title}>
              WEEKLY LESSON PLAN
            </Text>
          </View>
        </View>

        {/* METADATA */}
        <View style={pdfStyles.metadata}>
          <View style={pdfStyles.metadataItem}>
            <Text style={pdfStyles.metadataLabel}>
              PERIOD
            </Text>

            <Text style={pdfStyles.metadataValue}>
              {periodName}
            </Text>
          </View>

          <View style={pdfStyles.metadataItem}>
            <Text style={pdfStyles.metadataLabel}>
              TOTAL LESSON PLANS
            </Text>

            <Text style={pdfStyles.metadataValue}>
              {lessonPlans.length}
            </Text>
          </View>

          <View style={pdfStyles.metadataItem}>
            <Text style={pdfStyles.metadataLabel}>
              PREPARED BY
            </Text>

            <Text style={pdfStyles.metadataValue}>
              {teachers.length > 0
                ? teachers.join(", ")
                : "Teaching Staff"}
            </Text>
          </View>
        </View>

        {/* TABLE HEADER */}
        <View style={pdfStyles.tableHeader}>
          <Text style={[pdfStyles.cell, pdfStyles.topic]}>
            TOPIC
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.subject]}>
            SUBJECT
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.class]}>
            CLASS
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.period]}>
            PERIOD
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.week]}>
            WEEK
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.objectives]}>
            OBJECTIVES
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.content]}>
            CONTENT
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.method]}>
            METHOD
          </Text>

          <Text style={[pdfStyles.cell, pdfStyles.assessment]}>
            ASSESSMENT
          </Text>
        </View>

        {/* TABLE DATA */}
        {lessonPlans.map((plan, index) => (
          <View
            key={plan.id || index}
            style={[
              pdfStyles.tableRow,
              index % 2 === 0
                ? pdfStyles.tableRowAlt
                : {},
            ]}
          >
            <Text style={[pdfStyles.cell, pdfStyles.topic]}>
              {plan.topic || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.subject]}>
              {plan.subject_name || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.class]}>
              {plan.grade_class_name || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.period]}>
              {plan.period_name || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.week]}>
              {plan.week || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.objectives]}>
              {plan.objectives || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.content]}>
              {plan.content || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.method]}>
              {plan.teaching_method || "-"}
            </Text>

            <Text style={[pdfStyles.cell, pdfStyles.assessment]}>
              {plan.assessment || "-"}
            </Text>
          </View>
        ))}

        {/* FOOTER */}
        <View style={pdfStyles.footer}>
          <Text style={pdfStyles.footerText}>
            Official Weekly Lesson Plan Record
          </Text>

          <Text
            style={pdfStyles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

/*
|--------------------------------------------------------------------------
| MAIN COMPONENT
|--------------------------------------------------------------------------
*/

const ViewLessonPlan = () => {
  const [lessonPlans, setLessonPlans] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const accessToken = localStorage.getItem("access");

  /*
  |--------------------------------------------------------------------------
  | FETCH DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchLessonPlans();
    fetchPeriods();
  }, []);

  const fetchLessonPlans = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        "/lesson-plans/",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setLessonPlans(res.data);
    } catch (error) {
      Swal.fire(
        "Error",
        "Unable to load lesson plans.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriods = async () => {
    try {
      const res = await api.get(
        "/api/users/periods/",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setPeriods(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | FILTER LESSON PLANS
  |--------------------------------------------------------------------------
  */

  const filteredPlans = useMemo(() => {
    return lessonPlans.filter((plan) => {
      const matchesPeriod =
        !selectedPeriod ||
        plan.period_name === selectedPeriod;

      const searchText = search.toLowerCase();

      const matchesSearch =
        !search ||
        plan.topic?.toLowerCase().includes(searchText) ||
        plan.subject_name?.toLowerCase().includes(searchText) ||
        plan.grade_class_name?.toLowerCase().includes(searchText) ||
        plan.teacher_name?.toLowerCase().includes(searchText);

      return matchesPeriod && matchesSearch;
    });
  }, [
    lessonPlans,
    selectedPeriod,
    search,
  ]);

  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete Lesson Plan?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: COLORS.red,
      cancelButtonColor: COLORS.black,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(
            `/lesson-plans/${id}/`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          Swal.fire(
            "Deleted",
            "Lesson plan deleted successfully.",
            "success"
          );

          fetchLessonPlans();
        } catch (error) {
          Swal.fire(
            "Error",
            "Unable to delete lesson plan.",
            "error"
          );
        }
      }
    });
  };

  /*
  |--------------------------------------------------------------------------
  | EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = (plan) => {
    setEditingId(plan.id);

    setEditedData({
      ...plan,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | UPDATE
  |--------------------------------------------------------------------------
  */

  const handleUpdate = async (id) => {
    try {
      await api.put(
        `/lesson-plans/${id}/`,
        editedData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      Swal.fire(
        "Success",
        "Lesson plan updated successfully.",
        "success"
      );

      setEditingId(null);
      fetchLessonPlans();
    } catch (error) {
      Swal.fire(
        "Error",
        "Unable to update lesson plan.",
        "error"
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD PDF
  |--------------------------------------------------------------------------
  */

  const downloadPlans = selectedPeriod
    ? filteredPlans
    : lessonPlans;

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.cream,
        px: {
          xs: 1.5,
          sm: 3,
          md: 5,
        },
        py: {
          xs: 2,
          md: 4,
        },
      }}
    >
      {/* PAGE HEADER */}
      <Paper
        elevation={0}
        sx={{
          background:
            "linear-gradient(135deg, #111111 0%, #242424 100%)",
          borderRadius: {
            xs: 2,
            md: 3,
          },
          p: {
            xs: 2,
            md: 3,
          },
          mb: 3,
          color: COLORS.white,
          borderBottom: `5px solid ${COLORS.orange}`,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "flex-start",
            md: "center",
          }}
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: {
                  xs: "1.4rem",
                  sm: "1.8rem",
                  md: "2.1rem",
                },
                letterSpacing: "-0.5px",
              }}
            >
              My Lesson Plans
            </Typography>

            <Typography
              sx={{
                color: "#D1D5DB",
                mt: 0.5,
                fontSize: {
                  xs: "0.82rem",
                  md: "0.95rem",
                },
              }}
            >
              Manage, review, edit and download your weekly teaching plans.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: COLORS.orange,
              px: 2,
              py: 1,
              borderRadius: 2,
            }}
          >
            <MenuBook />

            <Typography
              sx={{
                fontWeight: "bold",
              }}
            >
              {lessonPlans.length} Plans
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* FILTER PANEL */}
      <Paper
        elevation={0}
        sx={{
          p: {
            xs: 1.5,
            md: 2,
          },
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.white,
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
        >
          {/* SEARCH */}
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search lesson plans"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* PERIOD */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Period"
              value={selectedPeriod}
              onChange={(e) =>
                setSelectedPeriod(e.target.value)
              }
            >
              <MenuItem value="">
                All Periods
              </MenuItem>

              {periods.map((period) => (
                <MenuItem
                  key={period.id}
                  value={period.name}
                >
                  {period.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* CLEAR */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Close />}
              onClick={() => {
                setSearch("");
                setSelectedPeriod("");
              }}
              sx={{
                height: 40,
                borderColor: COLORS.black,
                color: COLORS.black,
              }}
            >
              Clear
            </Button>
          </Grid>

          {/* DOWNLOAD */}
          <Grid item xs={12} md={2}>
            {downloadPlans.length > 0 ? (
              <PDFDownloadLink
                document={
                  <LessonPlanPDF
                    lessonPlans={downloadPlans}
                    periodName={
                      selectedPeriod ||
                      "All Periods"
                    }
                  />
                }
                fileName={
                  selectedPeriod
                    ? `lesson_plans_${selectedPeriod}.pdf`
                    : "all_lesson_plans.pdf"
                }
                style={{
                  textDecoration: "none",
                  width: "100%",
                }}
              >
                {({ loading: pdfLoading }) => (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Download />}
                    disabled={pdfLoading}
                    sx={{
                      height: 40,
                      backgroundColor: COLORS.orange,
                      color: COLORS.black,
                      fontWeight: "bold",
                      "&:hover": {
                        backgroundColor: COLORS.darkOrange,
                      },
                    }}
                  >
                    {pdfLoading
                      ? "Preparing..."
                      : "Download PDF"}
                  </Button>
                )}
              </PDFDownloadLink>
            ) : (
              <Button
                fullWidth
                disabled
                variant="contained"
              >
                No Data
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Chip
          icon={<MenuBook />}
          label={`${filteredPlans.length} Showing`}
          sx={{
            backgroundColor: COLORS.black,
            color: COLORS.white,
          }}
        />

        {selectedPeriod && (
          <Chip
            icon={<CalendarMonth />}
            label={selectedPeriod}
            sx={{
              backgroundColor: COLORS.lightCamel,
              color: COLORS.black,
            }}
          />
        )}
      </Box>

      {/* LOADING */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CircularProgress
            sx={{
              color: COLORS.orange,
            }}
          />
        </Box>
      ) : filteredPlans.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 3,
            border: `1px dashed ${COLORS.camel}`,
          }}
        >
          <MenuBook
            sx={{
              fontSize: 50,
              color: COLORS.camel,
              mb: 1,
            }}
          />

          <Typography
            variant="h6"
            fontWeight="bold"
          >
            No lesson plans found
          </Typography>

          <Typography
            color="text.secondary"
          >
            Try changing your search or period filter.
          </Typography>
        </Paper>
      ) : (
        <Grid
          container
          spacing={2}
        >
          {filteredPlans.map((plan) => (
            <Grid
              item
              xs={12}
              key={plan.id}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  transition: "0.25s ease",
                  "&:hover": {
                    boxShadow:
                      "0 10px 30px rgba(0,0,0,0.08)",
                    transform: {
                      md: "translateY(-2px)",
                    },
                  },
                }}
              >
                {/* CARD HEADER */}
                <Box
                  sx={{
                    backgroundColor: COLORS.black,
                    color: COLORS.white,
                    px: {
                      xs: 1.5,
                      md: 2.5,
                    },
                    py: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      fontWeight={900}
                      sx={{
                        fontSize: {
                          xs: "1rem",
                          md: "1.15rem",
                        },
                      }}
                    >
                      {plan.topic}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#D1D5DB",
                        fontSize: "0.8rem",
                      }}
                    >
                      {plan.subject_name} •{" "}
                      {plan.grade_class_name}
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Chip
                      label={`Week ${plan.week}`}
                      size="small"
                      sx={{
                        backgroundColor: COLORS.orange,
                        color: COLORS.black,
                        fontWeight: "bold",
                      }}
                    />

                    <IconButton
                      size="small"
                      onClick={() =>
                        handleEdit(plan)
                      }
                      sx={{
                        color: COLORS.white,
                        backgroundColor:
                          "rgba(255,255,255,0.1)",
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() =>
                        handleDelete(plan.id)
                      }
                      sx={{
                        color: "#FF8A80",
                        backgroundColor:
                          "rgba(255,255,255,0.1)",
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                {/* EDIT MODE */}
                {editingId === plan.id ? (
                  <Box
                    sx={{
                      p: {
                        xs: 2,
                        md: 3,
                      },
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                    >
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Topic"
                          value={
                            editedData.topic || ""
                          }
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              topic:
                                e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Week"
                          value={
                            editedData.week || ""
                          }
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              week:
                                e.target.value,
                            })
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          label="Period"
                          value={
                            editedData.period || ""
                          }
                          onChange={(e) =>
                            setEditedData({
                              ...editedData,
                              period:
                                e.target.value,
                            })
                          }
                        >
                          {periods.map(
                            (period) => (
                              <MenuItem
                                key={period.id}
                                value={period.id}
                              >
                                {period.name}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      </Grid>

                      {[
                        {
                          label: "Objectives",
                          field: "objectives",
                        },
                        {
                          label: "Content",
                          field: "content",
                        },
                        {
                          label: "Teaching Method",
                          field:
                            "teaching_method",
                        },
                        {
                          label: "Assessment",
                          field: "assessment",
                        },
                      ].map((item) => (
                        <Grid
                          item
                          xs={12}
                          key={item.field}
                        >
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={item.label}
                            value={
                              editedData[
                                item.field
                              ] || ""
                            }
                            onChange={(e) =>
                              setEditedData({
                                ...editedData,
                                [item.field]:
                                  e.target.value,
                              })
                            }
                          />
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={1}
                        >
                          <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={() =>
                              handleUpdate(
                                plan.id
                              )
                            }
                            sx={{
                              backgroundColor:
                                COLORS.orange,
                              color:
                                COLORS.black,
                              fontWeight:
                                "bold",
                            }}
                          >
                            Save Changes
                          </Button>

                          <Button
                            variant="outlined"
                            onClick={() =>
                              setEditingId(null)
                            }
                            sx={{
                              color:
                                COLORS.black,
                              borderColor:
                                COLORS.black,
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  /* VIEW MODE */
                  <Box
                    sx={{
                      p: {
                        xs: 1.5,
                        md: 2.5,
                      },
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                    >
                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<School />}
                          label="Class"
                          value={
                            plan.grade_class_name
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<CalendarMonth />}
                          label="Period"
                          value={
                            plan.period_name
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<MenuBook />}
                          label="Subject"
                          value={
                            plan.subject_name
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <InfoBlock
                          icon={<Assessment />}
                          label="Week"
                          value={
                            `Week ${plan.week}`
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            my: 0.5,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Objectives"
                          content={
                            plan.objectives
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Lesson Content"
                          content={
                            plan.content
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Teaching Method"
                          content={
                            plan.teaching_method
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <ContentBlock
                          title="Assessment"
                          content={
                            plan.assessment
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| INFO BLOCK
|--------------------------------------------------------------------------
*/

const InfoBlock = ({
  icon,
  label,
  value,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        backgroundColor: COLORS.lightCamel,
        p: 1.2,
        borderRadius: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          color: COLORS.darkOrange,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: COLORS.gray,
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontWeight: "bold",
            color: COLORS.black,
            fontSize: "0.9rem",
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| CONTENT BLOCK
|--------------------------------------------------------------------------
*/

const ContentBlock = ({
  title,
  content,
}) => {
  return (
    <Box
      sx={{
        borderLeft: `4px solid ${COLORS.orange}`,
        backgroundColor: COLORS.cream,
        p: 1.5,
        borderRadius: "0 8px 8px 0",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color: COLORS.black,
          mb: 0.5,
          fontSize: "0.9rem",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: COLORS.gray,
          fontSize: "0.88rem",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
        }}
      >
        {content || "No information provided."}
      </Typography>
    </Box>
  );
};

export default ViewLessonPlan;




// import React, { useState, useEffect } from 'react';  
// import api from '../../api/api';
// import {
//   Box,
//   Typography,
//   IconButton,
//   TextField,
//   Button,
//   Paper,
//   Grid,
//   MenuItem,
// } from '@mui/material';
// import { Edit, Delete, Download } from '@mui/icons-material';
// import Swal from 'sweetalert2';
// import jsPDF from 'jspdf';
// import logo from './savationChristLogo.png';

// const ViewLessonPlan = () => {
//   const [lessonPlans, setLessonPlans] = useState([]);
//   const [editingId, setEditingId] = useState(null);
//   const [editedData, setEditedData] = useState({});
//   const [periods, setPeriods] = useState([]);
//   const [selectedPeriod, setSelectedPeriod] = useState('');
//   const accessToken = localStorage.getItem("access");

//   useEffect(() => {
//     fetchLessonPlans();
//     fetchPeriods();
//   }, []);

//   const fetchLessonPlans = async () => {
//     const res = await api.get('/lesson-plans/', {
//       headers: { Authorization: `Bearer ${accessToken}` }
//     });
//     setLessonPlans(res.data);
//   };

//   const fetchPeriods = async () => {
//     const res = await api.get('/api/users/periods/', {
//       headers: { Authorization: `Bearer ${accessToken}` }
//     });
//     setPeriods(res.data);
//   };

//   const handleDelete = (id) => {
//     Swal.fire({
//       title: 'Are you sure?',
//       text: 'This action cannot be undone!',
//       icon: 'warning',
//       showCancelButton: true,
//       confirmButtonText: 'Yes, delete it!'
//     }).then(async (result) => {
//       if (result.isConfirmed) {
//         await api.delete(`/lesson-plans/${id}/`, {
//           headers: { Authorization: `Bearer ${accessToken}` }
//         });
//         Swal.fire('Deleted!', 'Lesson plan has been deleted.', 'success');
//         fetchLessonPlans();
//       }
//     });
//   };

//   const handleEdit = (plan) => {
//     setEditingId(plan.id);
//     setEditedData({ ...plan });
//   };

//   const handleUpdate = async (id) => {
//     try {
//       await api.put(`/lesson-plans/${id}/`, editedData, {
//         headers: { Authorization: `Bearer ${accessToken}` }
//       });
//       Swal.fire('Success', 'Lesson plan updated!', 'success');
//       setEditingId(null);
//       fetchLessonPlans();
//     } catch (err) {
//       Swal.fire('Error', 'Update failed!', 'error');
//     }
//   };

//   const handleDownload = () => {
//     const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
//     const pageWidth = doc.internal.pageSize.getWidth();
//     const pageHeight = doc.internal.pageSize.getHeight();

//     const marginX = 15;
//     const marginTop = 50;
//     const marginBottom = 20;
//     const colWidths = [30, 25, 20, 20, 12, 45, 45, 30, 30];

//     const columnColors = [
//       [255, 235, 238], [232, 245, 233], [227, 242, 253],
//       [255, 249, 196], [248, 237, 227], [240, 244, 195],
//       [209, 233, 252], [224, 224, 224], [252, 228, 236]
//     ];

//     const image = new Image();
//     image.src = logo;

//     image.onload = () => {
//       const drawHeader = () => {
//         doc.addImage(image, 'JPEG', marginX, 10, 30, 30);
//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(20);
//         doc.text("Salvation In Christ School System", pageWidth / 2, 16, { align: "center" });

//         doc.setFont('helvetica', 'normal');
//         doc.setFontSize(11);
//         doc.text("Location: Zazay Community, Paynesville City, Monrovia", pageWidth / 2, 22, { align: "center" });
//         doc.text("Contact: +231-886909466/+231-770123025 | Email: salvationinchristministries69@gmail.com", pageWidth / 2, 28, { align: "center" });

//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(14);
//         doc.text("Weekly Lesson Plan", pageWidth / 2, 38, { align: "center" });
//       };

//       const drawTableHeaders = (doc, startX, startY, colWidths) => {
//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(10);
//         const headers = ["Topic", "Subject", "Class", "Period", "Week", "Objectives", "Content", "Method", "Assessment"];
//         let x = startX;
//         doc.setTextColor(255, 255, 255);
//         headers.forEach((header, i) => {
//           doc.setFillColor(0, 0, 0);
//           doc.rect(x, startY, colWidths[i], 10, 'F');
//           doc.text(header, x + 2, startY + 7);
//           x += colWidths[i];
//         });
//         doc.setTextColor(0, 0, 0); // reset text color for content
//       };

//       drawHeader();

//       let currentY = marginTop;
//       drawTableHeaders(doc, marginX, currentY, colWidths);
//       currentY += 10;

//       doc.setFont('helvetica', 'normal');
//       doc.setFontSize(9);

//       for (let i = 0; i < lessonPlans.length; i++) {
//         const plan = lessonPlans[i];
//         const cells = [
//           plan.topic, plan.subject_name, plan.grade_class_name, plan.period_name,
//           plan.week?.toString() || '', plan.objectives, plan.content,
//           plan.teaching_method, plan.assessment,
//         ];

//         const wrappedTexts = cells.map((text, idx) => {
//           const content = text || '';
//           return doc.splitTextToSize(content, colWidths[idx] - 4);
//         });

//         const dynamicRowHeight = Math.max(...wrappedTexts.map(t => t.length)) * 5 + 2;

//         // <-- FIX: Only add page if there's a next row -->
//         if (currentY + dynamicRowHeight > pageHeight - marginBottom && i < lessonPlans.length - 1) {
//           doc.addPage();
//           drawHeader();
//           currentY = marginTop;
//           drawTableHeaders(doc, marginX, currentY, colWidths);
//           currentY += 10;
//         }

//         let x = marginX;
//         wrappedTexts.forEach((lines, idx) => {
//           doc.setFillColor(...columnColors[idx]);
//           doc.rect(x, currentY, colWidths[idx], dynamicRowHeight, 'F');
//           lines.forEach((line, lineIdx) => {
//             doc.text(line, x + 2, currentY + 5 + (lineIdx * 5));
//           });
//           x += colWidths[idx];
//         });

//         currentY += dynamicRowHeight;
//       }

//       const uniqueTeachers = [...new Set(lessonPlans.map(plan => plan.teacher_name).filter(Boolean))];

//       if (uniqueTeachers.length > 0) {
//         const teacherText = `Prepared by: ${uniqueTeachers.join(', ')}`;
//         doc.setFontSize(10);
//         doc.setFont('helvetica', 'bold');
//         doc.text(teacherText, marginX, pageHeight - 10);
//       }

//       doc.save('lesson_plans_table.pdf');
//     };
//   };

//   // Fixed function for downloading lesson plans by period
//   const handleDownloadByPeriod = (periodName) => {
//     const filteredPlans = lessonPlans.filter(
//       (plan) => plan.period_name === periodName
//     );

//     if (filteredPlans.length === 0) {
//       Swal.fire('No Data', 'No lesson plans found for the selected period.', 'info');
//       return; // Don't generate PDF if no data
//     }

//     const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
//     const pageWidth = doc.internal.pageSize.getWidth();
//     const pageHeight = doc.internal.pageSize.getHeight();

//     const marginX = 15;
//     const marginTop = 50;
//     const marginBottom = 20;
//     const colWidths = [30, 25, 20, 20, 12, 45, 45, 30, 30];

//     const columnColors = [
//       [255, 235, 238], [232, 245, 233], [227, 242, 253],
//       [255, 249, 196], [248, 237, 227], [240, 244, 195],
//       [209, 233, 252], [224, 224, 224], [252, 228, 236]
//     ];

//     const image = new Image();
//     image.src = logo;

//     image.onload = () => {
//       const drawHeader = () => {
//         doc.addImage(image, 'JPEG', marginX, 10, 30, 30);
//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(20);
//         doc.text("Jene Williams School", pageWidth / 2, 16, { align: "center" });

//         doc.setFont('helvetica', 'normal');
//         doc.setFontSize(11);
//         doc.text("123 Academic Avenue, Johnsonville, Liberia", pageWidth / 2, 22, { align: "center" });
//         doc.text("Contact: +231-776-000-000 | jene.williams@example.com", pageWidth / 2, 28, { align: "center" });

//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(14);
//         doc.text(`Weekly Lesson Plan - Period: ${periodName}`, pageWidth / 2, 38, { align: "center" });
//       };

//       const drawTableHeaders = (doc, startX, startY, colWidths) => {
//         doc.setFont('helvetica', 'bold');
//         doc.setFontSize(10);
//         const headers = ["Topic", "Subject", "Class", "Period", "Week", "Objectives", "Content", "Method", "Assessment"];
//         let x = startX;
//         doc.setTextColor(255, 255, 255);
//         headers.forEach((header, i) => {
//           doc.setFillColor(0, 0, 0);
//           doc.rect(x, startY, colWidths[i], 10, 'F');
//           doc.text(header, x + 2, startY + 7);
//           x += colWidths[i];
//         });
//         doc.setTextColor(0, 0, 0); // reset text color for content
//       };

//       drawHeader();

//       let currentY = marginTop;
//       drawTableHeaders(doc, marginX, currentY, colWidths);
//       currentY += 10;

//       doc.setFont('helvetica', 'normal');
//       doc.setFontSize(9);

//       for (let i = 0; i < filteredPlans.length; i++) {
//         const plan = filteredPlans[i];
//         const cells = [
//           plan.topic, plan.subject_name, plan.grade_class_name, plan.period_name,
//           plan.week?.toString() || '', plan.objectives, plan.content,
//           plan.teaching_method, plan.assessment,
//         ];

//         const wrappedTexts = cells.map((text, idx) => {
//           const content = text || '';
//           return doc.splitTextToSize(content, colWidths[idx] - 4);
//         });

//         const dynamicRowHeight = Math.max(...wrappedTexts.map(t => t.length)) * 5 + 2;

//         // <-- FIX: Only add page if there's a next row -->
//         if (currentY + dynamicRowHeight > pageHeight - marginBottom && i < filteredPlans.length - 1) {
//           doc.addPage();
//           drawHeader();
//           currentY = marginTop;
//           drawTableHeaders(doc, marginX, currentY, colWidths);
//           currentY += 10;
//         }

//         let x = marginX;
//         wrappedTexts.forEach((lines, idx) => {
//           doc.setFillColor(...columnColors[idx]);
//           doc.rect(x, currentY, colWidths[idx], dynamicRowHeight, 'F');
//           lines.forEach((line, lineIdx) => {
//             doc.text(line, x + 2, currentY + 5 + (lineIdx * 5));
//           });
//           x += colWidths[idx];
//         });

//         currentY += dynamicRowHeight;
//       }

//       const uniqueTeachers = [...new Set(filteredPlans.map(plan => plan.teacher_name).filter(Boolean))];
//       if (uniqueTeachers.length > 0) {
//         const teacherText = `Prepared by: ${uniqueTeachers.join(', ')}`;
//         doc.setFontSize(10);
//         doc.setFont('helvetica', 'bold');
//         doc.text(teacherText, marginX, pageHeight - 10);
//       }

//       doc.save(`lesson_plans_${periodName}.pdf`);
//     };
//   };

//   return (
//     <Box sx={{ mt: 4, px: { xs: 2, md: 4 } }}>
//       <Typography variant="h5" gutterBottom>
//         My Lesson Plans
//       </Typography>

//       {/* Period Filter and Download Button */}
//       <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
//         <TextField
//           select
//           label="Filter by Period"
//           value={selectedPeriod}
//           onChange={(e) => setSelectedPeriod(e.target.value)}
//           size="small"
//           sx={{ minWidth: 200 }}
//         >
//           <MenuItem value="">All Periods</MenuItem>
//           {periods.map((p) => (
//             <MenuItem key={p.id} value={p.name}>
//               {p.name}
//             </MenuItem>
//           ))}
//         </TextField>

//         <Button
//           variant="contained"
//           color="primary"
//           onClick={() => handleDownloadByPeriod(selectedPeriod)}
//           disabled={!selectedPeriod}
//         >
//           Download Period PDF
//         </Button>
//       </Box>



//       {/* Header Grid */}
//       <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', mb: 1 }}>
//         <Grid container spacing={1}>
//           {['Topic', 'Subject', 'Class', 'Period', 'Week', 'Objectives', 'Content', 'Method', 'Assessment', 'Actions'].map((header) => (
//             <Grid key={header} item xs={12} sm={6} md={1.2}>
//               <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
//                 {header}
//               </Typography>
//             </Grid>
//           ))}
//         </Grid>
//       </Paper>

//       {lessonPlans.map((plan) => (
//         <Paper
//           key={plan.id}
//           sx={{
//             mb: 2,
//             p: 2,
//             backgroundColor: '#f9f9f9',
//             borderLeft: '6px solid #90caf9',
//           }}
//         >
//           {editingId === plan.id ? (
//             <Grid container spacing={2}>
//               <Grid item xs={12} md={3}>
//                 <TextField
//                   label="Topic"
//                   fullWidth
//                   value={editedData.topic}
//                   onChange={(e) => setEditedData({ ...editedData, topic: e.target.value })}
//                 />
//               </Grid>
//               <Grid item xs={12} md={3}>
//                 <TextField
//                   label="Week"
//                   fullWidth
//                   value={editedData.week}
//                   onChange={(e) => setEditedData({ ...editedData, week: e.target.value })}
//                 />
//               </Grid>
//               <Grid item xs={12} md={3}>
//                 <TextField
//                   select
//                   label="Period"
//                   fullWidth
//                   value={editedData.period}
//                   onChange={(e) => setEditedData({ ...editedData, period: e.target.value })}
//                 >
//                   {periods.map((p) => (
//                     <MenuItem key={p.id} value={p.id}>
//                       {p.name}
//                     </MenuItem>
//                   ))}
//                 </TextField>
//               </Grid>
//               <Grid item xs={12}>
//                 <TextField
//                   multiline
//                   fullWidth
//                   label="Objectives"
//                   minRows={2}
//                   value={editedData.objectives}
//                   onChange={(e) => setEditedData({ ...editedData, objectives: e.target.value })}
//                 />
//               </Grid>
//               <Grid item xs={12}>
//                 <TextField
//                   multiline
//                   fullWidth
//                   label="Content"
//                   minRows={2}
//                   value={editedData.content}
//                   onChange={(e) => setEditedData({ ...editedData, content: e.target.value })}
//                 />
//               </Grid>
//               <Grid item xs={12}>
//                 <TextField
//                   multiline
//                   fullWidth
//                   label="Teaching Method"
//                   minRows={2}
//                   value={editedData.teaching_method}
//                   onChange={(e) => setEditedData({ ...editedData, teaching_method: e.target.value })}
//                 />
//               </Grid>
//               <Grid item xs={12}>
//                 <TextField
//                   multiline
//                   fullWidth
//                   label="Assessment"
//                   minRows={2}
//                   value={editedData.assessment}
//                   onChange={(e) => setEditedData({ ...editedData, assessment: e.target.value })}
//                 />
//               </Grid>
//               <Grid item xs={12}>
//                 <Button variant="contained" onClick={() => handleUpdate(plan.id)}>
//                   Update
//                 </Button>
//               </Grid>
//             </Grid>
//           ) : (
//             <Grid container spacing={1} alignItems="flex-start">
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.topic}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.subject_name}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.grade_class_name}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.period_name}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.week}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.objectives}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.content}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.teaching_method}</Typography></Grid>
//               <Grid item xs={12} sm={6} md={1.2}><Typography>{plan.assessment}</Typography></Grid>

//               <Grid item xs={12} md={1.2}>
//                 <IconButton color="primary" onClick={() => handleEdit(plan)}>
//                   <Edit />
//                 </IconButton>
//                 <IconButton color="error" onClick={() => handleDelete(plan.id)}>
//                   <Delete />
//                 </IconButton>
//               </Grid>
//             </Grid>
//           )}
//         </Paper>
//       ))}
//     </Box>
//   );
// };

// export default ViewLessonPlan;




